import { useState, useEffect, useCallback } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { 
  Brain, ArrowLeft, Trophy, Target, Zap, Clock, 
  CheckCircle2, XCircle, TrendingUp, TrendingDown,
  BarChart3, Play, Users, Sparkles, ChevronRight
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

const PHASES = {
  SETUP: 'setup',
  LOADING: 'loading',
  PLAYING: 'playing',
  ANSWER_REVEAL: 'answer_reveal',
  RESULTS: 'results'
};

// Loading messages for Beat the Data
const LOADING_MESSAGES = [
  { text: "Analyzing historical data...", icon: BarChart3 },
  { text: "Preparing contestant comparisons...", icon: Users },
  { text: "Crafting unique clues...", icon: Sparkles },
  { text: "Setting up your challenge...", icon: Target },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', description: '$200-$400 clues', color: 'text-green-400 bg-green-500/20' },
  { value: 'medium', label: 'Medium', description: '$400-$800 clues', color: 'text-yellow-400 bg-yellow-500/20' },
  { value: 'hard', label: 'Hard', description: '$800+ clues', color: 'text-red-400 bg-red-500/20' },
  { value: 'mixed', label: 'Mixed', description: 'All difficulty levels', color: 'text-purple-400 bg-purple-500/20' }
];

export default function BeatTheData() {
  useSEO('Beat the Data Challenge | JepPrep', 'Can you outperform historical game show contestants? Answer paraphrased clues and compare your accuracy.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [phase, setPhase] = useState(PHASES.SETUP);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  
  // Setup state
  const [difficulty, setDifficulty] = useState('mixed');
  const [numClues, setNumClues] = useState(10);
  
  // Game state
  const [sessionId, setSessionId] = useState(null);
  const [clues, setClues] = useState([]);
  const [currentClueIndex, setCurrentClueIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [answerResult, setAnswerResult] = useState(null);
  const [sessionResults, setSessionResults] = useState(null);
  const [answeredClues, setAnsweredClues] = useState([]);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await api.get(`/api/v1/askalex/beatthedata/stats?user_id=${user.id}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const startGame = async () => {
    setPhase(PHASES.LOADING);
    setLoadingProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0]);
    
    // Animate progress and cycle messages
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => Math.min(prev + 12, 90));
    }, 400);
    
    const messageInterval = setInterval(() => {
      setLoadingMessage(prev => {
        const currentIdx = LOADING_MESSAGES.findIndex(m => m.text === prev.text);
        return LOADING_MESSAGES[(currentIdx + 1) % LOADING_MESSAGES.length];
      });
    }, 1000);
    
    try {
      const res = await api.post(`/api/v1/askalex/beatthedata/session/start?user_id=${user.id}`, {
        difficulty_tier: difficulty,
        num_clues: numClues
      });
      
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      setLoadingProgress(100);
      
      // Small delay to show 100% completion
      setTimeout(() => {
        setSessionId(res.data.session_id);
        setClues(res.data.clues);
        setCurrentClueIndex(0);
        setAnsweredClues([]);
        setPhase(PHASES.PLAYING);
        setStartTime(Date.now());
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      console.error('Error starting game:', error);
      alert('Failed to start game. Please try again.');
      setPhase(PHASES.SETUP);
    }
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim()) return;
    
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    const currentClue = clues[currentClueIndex];
    
    try {
      const res = await api.post(`/api/v1/askalex/beatthedata/session/${sessionId}/answer?user_id=${user.id}`, {
        session_id: sessionId,
        clue_id: currentClue.clue_id,
        user_answer: userAnswer,
        time_taken_seconds: timeTaken
      });
      
      setAnswerResult(res.data);
      setAnsweredClues([...answeredClues, {
        ...currentClue,
        userAnswer,
        isCorrect: res.data.is_correct,
        correctAnswer: res.data.correct_answer
      }]);
      setPhase(PHASES.ANSWER_REVEAL);
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const nextClue = () => {
    setUserAnswer('');
    setAnswerResult(null);
    
    if (currentClueIndex < clues.length - 1) {
      setCurrentClueIndex(currentClueIndex + 1);
      setPhase(PHASES.PLAYING);
      setStartTime(Date.now());
    } else {
      completeSession();
    }
  };

  const completeSession = async () => {
    try {
      const res = await api.post(`/api/v1/askalex/beatthedata/session/${sessionId}/complete?user_id=${user.id}`);
      setSessionResults(res.data);
      setPhase(PHASES.RESULTS);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error completing session:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && phase === PHASES.PLAYING && userAnswer.trim()) {
      submitAnswer();
    }
  };

  const currentClue = clues[currentClueIndex];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/askalex">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12 hidden sm:block" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-display text-white truncate">Beat the Data</h1>
                <p className="text-gray-400 text-xs hidden sm:block">Outperform historical contestants</p>
              </div>
            </div>
            
            {phase === PHASES.PLAYING && (
              <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                <Badge className="bg-[hsl(var(--brand-blue-800))] text-gray-300 text-xs">
                  {currentClueIndex + 1}/{clues.length}
                </Badge>
                <Progress 
                  value={(currentClueIndex / clues.length) * 100} 
                  className="w-16 sm:w-32 h-2"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        
        {/* SETUP PHASE */}
        {phase === PHASES.SETUP && (
          <div className="space-y-6">
            {/* Stats Overview */}
            {stats && stats.total_sessions > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
                    <p className="text-2xl font-bold text-white">{stats.overall_accuracy}%</p>
                    <p className="text-gray-400 text-xs">Accuracy</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <Trophy className="w-6 h-6 mx-auto mb-2 text-emerald-400" />
                    <p className="text-2xl font-bold text-white">{stats.beat_rate}%</p>
                    <p className="text-gray-400 text-xs">Beat Rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <BarChart3 className="w-6 h-6 mx-auto mb-2 text-cyan-400" />
                    <p className="text-2xl font-bold text-white">{stats.total_sessions}</p>
                    <p className="text-gray-400 text-xs">Sessions</p>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4 text-center">
                    <Sparkles className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                    <p className="text-2xl font-bold text-white">{stats.total_xp}</p>
                    <p className="text-gray-400 text-xs">XP Earned</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Game Setup */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Beat the Data</h2>
                  <p className="text-gray-400 max-w-md mx-auto">
                    Answer AI-paraphrased clues and compare your performance to historical Jeopardy! contestant averages.
                  </p>
                </div>

                {/* Difficulty Selection */}
                <div className="mb-6">
                  <label className="text-white font-medium mb-3 block">Difficulty</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DIFFICULTY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setDifficulty(opt.value)}
                        className={`p-4 rounded-xl border transition-all ${
                          difficulty === opt.value
                            ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))]/10'
                            : 'border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 hover:bg-[hsl(var(--brand-blue-800))]'
                        }`}
                      >
                        <Badge className={`${opt.color} mb-2`}>{opt.label}</Badge>
                        <p className="text-white text-xs">{opt.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Number of Clues */}
                <div className="mb-8">
                  <label className="text-white font-medium mb-3 block">Number of Clues</label>
                  <div className="flex gap-3">
                    {[5, 10, 15, 20].map((n) => (
                      <button
                        key={n}
                        onClick={() => setNumClues(n)}
                        className={`flex-1 py-3 rounded-xl border transition-all font-medium ${
                          numClues === n
                            ? 'border-[hsl(var(--brand-gold-500))] bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]'
                            : 'border-[hsl(var(--border))] text-white hover:border-[hsl(var(--brand-gold-500))]/50 hover:bg-[hsl(var(--brand-blue-800))]'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={startGame}
                  disabled={loading}
                  className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                  data-testid="start-game-btn"
                >
                  {loading ? 'Preparing Clues...' : 'Start Challenge'}
                  <Play className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* LOADING PHASE */}
        {phase === PHASES.LOADING && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="flex justify-center"
          >
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] max-w-md w-full">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  {/* Animated Icon */}
                  <motion.div 
                    className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center"
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    {loadingMessage.icon && (
                      <loadingMessage.icon className="w-10 h-10 text-white" />
                    )}
                  </motion.div>
                  
                  {/* Title */}
                  <div>
                    <h2 className="text-xl font-display text-white mb-2">
                      Preparing Your Challenge
                    </h2>
                    <motion.p 
                      key={loadingMessage.text}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-gray-400"
                    >
                      {loadingMessage.text}
                    </motion.p>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <Progress 
                      value={loadingProgress} 
                      className="h-2 bg-[hsl(var(--accent))]"
                    />
                    <p className="text-xs text-gray-500">
                      {loadingProgress < 100 ? 'Loading clues...' : 'Ready!'}
                    </p>
                  </div>
                  
                  {/* Fun Fact */}
                  <div className="bg-[hsl(var(--accent))] rounded-lg p-4 text-left">
                    <p className="text-xs text-gray-400 mb-1">Did you know?</p>
                    <p className="text-sm text-gray-300">
                      Historical contestant accuracy varies by category - some topics are universally harder than others!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* PLAYING PHASE */}
        {phase === PHASES.PLAYING && currentClue && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-4 sm:p-8">
              {/* Category & Contestant Accuracy */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-6">
                <Badge className="bg-[hsl(var(--brand-blue-800))] text-gray-300 self-start">
                  {currentClue.category}
                </Badge>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-gray-400">Contestants:</span>
                  <span className={`font-medium whitespace-nowrap ${
                    currentClue.contestant_accuracy >= 70 ? 'text-green-400' :
                    currentClue.contestant_accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {currentClue.contestant_accuracy}% correct
                  </span>
                </div>
              </div>

              {/* Clue */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-4 sm:p-6 mb-6">
                <p className="text-white text-base sm:text-xl leading-relaxed text-center">
                  {currentClue.paraphrased_question}
                </p>
              </div>

              {/* Value Badge */}
              <div className="flex justify-center mb-6">
                <Badge className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))]">
                  ${currentClue.value}
                </Badge>
              </div>

              {/* Answer Input */}
              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="What is..."
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-lg py-6 text-center"
                  autoFocus
                  data-testid="answer-input"
                />
                
                <Button
                  onClick={submitAnswer}
                  disabled={!userAnswer.trim()}
                  className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold disabled:opacity-50"
                  data-testid="submit-answer-btn"
                >
                  Submit Answer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ANSWER REVEAL PHASE */}
        {phase === PHASES.ANSWER_REVEAL && answerResult && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8">
              {/* Result Header */}
              <div className={`text-center mb-6 p-6 rounded-xl ${
                answerResult.is_correct 
                  ? 'bg-emerald-500/10 border border-emerald-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  answerResult.is_correct ? 'bg-emerald-500/20' : 'bg-red-500/20'
                }`}>
                  {answerResult.is_correct ? (
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-400" />
                  )}
                </div>
                
                <h3 className={`text-2xl font-bold mb-2 ${
                  answerResult.is_correct ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {answerResult.is_correct ? 'Correct!' : 'Incorrect'}
                </h3>
                
                <p className="text-white text-lg">
                  The answer is: <span className="font-bold">{answerResult.correct_answer}</span>
                </p>
              </div>

              {/* Comparison to Contestants */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-400">Historical contestants:</span>
                  </div>
                  <span className={`font-bold ${
                    answerResult.contestant_accuracy >= 70 ? 'text-green-400' :
                    answerResult.contestant_accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {answerResult.contestant_accuracy}% got this right
                  </span>
                </div>
                
                {answerResult.beat_the_data && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-[hsl(var(--brand-gold-500))]">
                    <Trophy className="w-5 h-5" />
                    <span className="font-medium">You beat the data on this one!</span>
                  </div>
                )}
              </div>

              {/* Progress */}
              <div className="text-center text-gray-400 mb-6">
                {answerResult.answers_submitted} of {answerResult.total_clues} clues answered
              </div>

              <Button
                onClick={nextClue}
                className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg font-semibold"
                data-testid="next-clue-btn"
              >
                {currentClueIndex < clues.length - 1 ? 'Next Clue' : 'See Results'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* RESULTS PHASE */}
        {phase === PHASES.RESULTS && sessionResults && (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-8 text-center">
              {/* Trophy or Consolation */}
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                sessionResults.beat_the_data ? 'bg-[hsl(var(--brand-gold-500))]/20' : 'bg-gray-500/20'
              }`}>
                {sessionResults.beat_the_data ? (
                  <Trophy className="w-10 h-10 text-[hsl(var(--brand-gold-500))]" />
                ) : (
                  <TrendingDown className="w-10 h-10 text-gray-400" />
                )}
              </div>

              <h2 className="text-2xl font-bold text-white mb-2">
                {sessionResults.beat_the_data ? 'You Beat the Data!' : 'Contestants Win This Round'}
              </h2>
              <p className="text-gray-400 mb-8">{sessionResults.message}</p>

              {/* Score Comparison */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-gray-400 text-sm mb-1">Your Accuracy</p>
                    <p className={`text-3xl font-bold ${
                      sessionResults.user_accuracy >= sessionResults.contestant_avg_accuracy 
                        ? 'text-emerald-400' : 'text-white'
                    }`}>
                      {sessionResults.user_accuracy}%
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {sessionResults.user_correct}/{sessionResults.user_total} correct
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                  <CardContent className="p-4">
                    <p className="text-gray-400 text-sm mb-1">Contestant Average</p>
                    <p className="text-3xl font-bold text-cyan-400">
                      {sessionResults.contestant_avg_accuracy}%
                    </p>
                    <p className="text-gray-500 text-xs mt-1">Historical average</p>
                  </CardContent>
                </Card>
              </div>

              {/* Margin & XP */}
              <div className="flex items-center justify-center gap-6 mb-8">
                <div className="text-center">
                  <p className={`text-2xl font-bold ${
                    sessionResults.margin >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {sessionResults.margin >= 0 ? '+' : ''}{sessionResults.margin}%
                  </p>
                  <p className="text-gray-400 text-sm">Margin</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-[hsl(var(--brand-gold-500))]">
                    +{sessionResults.xp_earned}
                  </p>
                  <p className="text-gray-400 text-sm">XP Earned</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/askalex')}
                  variant="outline"
                  className="flex-1 border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--brand-blue-800))] py-6"
                >
                  Back to AskAlex
                </Button>
                <Button
                  onClick={() => {
                    setPhase(PHASES.SETUP);
                    setSessionResults(null);
                    setClues([]);
                  }}
                  className="flex-1 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6"
                >
                  Play Again
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
